import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  TablePagination,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Grid,
  Divider,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import lifeDeclarationService from '../services/lifeDeclarationService';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const LifeDeclaration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for declarations list
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    demise_status: '',
    life_cycle_status: '',
    min_failures: '',
    notification_sent: '',
    has_certificate: '',
  });
  const filtersRef = useRef(filters);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Action dialog state
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedDeclaration, setSelectedDeclaration] = useState(null);
  const [selectedActionType, setSelectedActionType] = useState('');
  const [actionFormData, setActionFormData] = useState({
    demise_status: '',
    admin_remarks: '',
    reset_failure_count: false,
    trigger_nominee_notification: false,
    supportingDoc: null,
  });
  const [actionLoading, runSaveAction] = useActionLock();

  // View details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Certificate upload dialog state
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [certificateFormData, setCertificateFormData] = useState({
    certificate_file: null,
    admin_remarks: '',
    mark_as_valid: false,
    update_demise_status: false,
  });
  const [certificateLoading, runUploadCertificate] = useActionLock();

  // PDF Preview state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Nominees preview state
  const [nomineesList, setNomineesList] = useState([]);
  const [selectedNominee, setSelectedNominee] = useState(null);
  const [nomineesLoading, setNomineesLoading] = useState(false);
  const [previewViewMode, setPreviewViewMode] = useState('nominees'); // 'nominees' or 'pdf'

  // Mobile card expansion state
  const [expandedCardId, setExpandedCardId] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Build API params from filter state (used so Apply uses latest values via ref)
  const buildFilterParams = useCallback((filterState, pageNum, search) => {
    const params = {
      page: pageNum + 1,
      page_size: rowsPerPage,
    };
    if (search) params.search = search;
    if (filterState.demise_status) params.demise_status = filterState.demise_status;
    if (filterState.life_cycle_status) params.life_cycle_status = filterState.life_cycle_status;
    const minFail = filterState.min_failures;
    if (minFail !== '' && minFail != null && !Number.isNaN(Number(minFail))) {
      params.min_failures = parseInt(minFail, 10);
    }
    if (filterState.notification_sent !== '' && filterState.notification_sent != null) {
      params.notification_sent = filterState.notification_sent === 'true';
    }
    if (filterState.has_certificate !== '' && filterState.has_certificate != null) {
      params.has_certificate = filterState.has_certificate === 'true';
    }
    return params;
  }, [rowsPerPage]);

  // Fetch declarations (overrideFilters: when applying, pass latest filters so they're not stale)
  const fetchDeclarations = useCallback(async (customPage, overrideFilters = null) => {
    setLoading(true);
    setError(null);

    const pageToUse = typeof customPage === 'number' ? customPage : page;
    const filterState = overrideFilters != null ? overrideFilters : filters;
    const search = overrideFilters != null ? searchQuery : searchQuery;
    const params = buildFilterParams(filterState, pageToUse, search);

    const result = await lifeDeclarationService.getDueLifeDeclarations(params);

    if (result.success) {
      setDeclarations(result.data.results || []);
      setTotalCount(result.data.count || 0);
    } else {
      setError(result.error);
      setDeclarations([]);
    }

    setLoading(false);
  }, [page, rowsPerPage, searchQuery, filters, buildFilterParams]);

  // Keep filtersRef in sync with filters (e.g. after clearFilters)
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initial fetch and when page/rowsPerPage changes
  useEffect(() => {
    fetchDeclarations();
  }, [page, rowsPerPage]);

  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      if (page === 0) {
        fetchDeclarations(0);
      } else {
        setPage(0);
      }
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes (keep ref in sync so Apply uses latest values)
  const handleFilterChange = (field, value) => {
    const next = { ...filters, [field]: value };
    setFilters(next);
    filtersRef.current = next;
  };

  const applyFilters = () => {
    setFilterDialogOpen(false);
    setPage(0);
    // Use ref so we send the filters just selected (avoids stale closure)
    fetchDeclarations(0, filtersRef.current);
  };

  const clearFilters = () => {
    const cleared = {
      demise_status: '',
      life_cycle_status: '',
      min_failures: '',
      notification_sent: '',
      has_certificate: '',
    };
    setFilters(cleared);
    filtersRef.current = cleared;
    setSearchQuery('');
    setPage(0);
    setFilterDialogOpen(false);
    fetchDeclarations(0, cleared, '');
  };

  const handleRefresh = () => {
    const cleared = {
      demise_status: '',
      life_cycle_status: '',
      min_failures: '',
      notification_sent: '',
      has_certificate: '',
    };
    setFilters(cleared);
    filtersRef.current = cleared;
    setSearchQuery('');
    setPage(0);
    fetchDeclarations(0, cleared, '');
  };

  // Action dialog handlers
  const handleActionClick = (declaration) => {
    setSelectedDeclaration(declaration);
    setSelectedActionType('');
    setActionFormData({
      demise_status: declaration.demise_status || '',
      admin_remarks: '',
      reset_failure_count: false,
      trigger_nominee_notification: false,
      supportingDoc: null,
    });
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setSelectedDeclaration(null);
    setSelectedActionType('');
    if (pdfPreviewUrl && pdfPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setNomineesList([]);
    setSelectedNominee(null);
    setPreviewViewMode('nominees');
  };

  // Check if action option should be disabled
  const isActionDisabled = (actionType, declaration) => {
    if (!declaration) return true;

    // 1) Prefer backend rules if available_actions is provided
    const backendAction = declaration.available_actions?.find(
      (action) => action.action_type === actionType
    );
    if (backendAction) {
      // disabled when backend says enabled === false
      return !backendAction.enabled;
    }

    // 2) Fallback to local rules (kept for backward compatibility)
    const demiseStatus = declaration.demise_status?.toLowerCase();
    const certificateValidity = declaration.death_certificate_validity?.toLowerCase();

    switch (actionType) {
      case 'mark_life_declaration':
        // Disable if demise status is confirmed
        return demiseStatus === 'confirmed' || demiseStatus === 'confirmed_deceased';
      case 'mark_demise_confirmation':
        // Only disable if already confirmed deceased
        return demiseStatus === 'confirmed' || demiseStatus === 'confirmed_deceased';
      case 'upload_death_certificate':
        // Allow upload by default if backend didn't specify
        return false;
      case 'preview':
        // Enable only when death certificate is marked Valid
        return certificateValidity !== 'valid';
      case 'generate_share':
        // Enable only when death certificate is marked Valid
        return certificateValidity !== 'valid';
      default:
        return false;
    }
  };

  const handleActionTypeChange = (actionType) => {
    setSelectedActionType(actionType);

    // Handle specific actions that don't need the form
    if (actionType === 'preview') {
      // Handle preview action
      handlePreviewCertificate(selectedDeclaration);
    }
    // For mark_life_declaration, mark_demise_confirmation, upload_death_certificate, and generate_share
    // the form will be shown so user can optionally upload supporting document
  };

  const handlePreviewCertificate = async (declaration) => {
    setNomineesLoading(true);
    setNomineesList([]);
    setPreviewViewMode('nominees');
    setPdfPreviewUrl(null);
    setSelectedNominee(null);

    try {
      const result = await lifeDeclarationService.takeAction(
        declaration.user_id || declaration.id,
        { action_type: 'preview' }
      );

      if (result.success && result.data) {
        // Check if response contains nominees list
        if (result.data.nominees && Array.isArray(result.data.nominees)) {
          setNomineesList(result.data.nominees);
          if (result.data.nominees.length === 0) {
            setSnackbar({
              open: true,
              message: 'No nominees found for PDF preview',
              severity: 'info',
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: result.data.message || 'Nominees loaded successfully',
            severity: 'success',
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to load nominees',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load nominees for preview',
        severity: 'error',
      });
    }

    setNomineesLoading(false);
  };

  // Handle nominee click to fetch PDF
  const handleNomineeClick = async (nominee) => {
    setSelectedNominee(nominee);
    setPdfLoading(true);
    // Revoke previous blob URL to prevent memory leaks
    if (pdfPreviewUrl && pdfPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setPreviewViewMode('pdf');

    try {
      const userId = selectedDeclaration?.user_id || selectedDeclaration?.id;
      const result = await lifeDeclarationService.getNomineePdfPreview(userId, nominee.nominee_id);

      if (result.success && result.data) {
        if (result.data.pdf_url) {
          // Fetch PDF via authenticated proxy as blob
          const blobResult = await lifeDeclarationService.getPreviewPdfBlob(result.data.pdf_url);
          if (blobResult.success && blobResult.data?.blobUrl) {
            setPdfPreviewUrl(blobResult.data.blobUrl);
          } else {
            setSnackbar({
              open: true,
              message: blobResult.error || 'Failed to download PDF',
              severity: 'error',
            });
            setPreviewViewMode('nominees');
          }
        } else {
          setSnackbar({
            open: true,
            message: 'PDF URL not found in response',
            severity: 'error',
          });
          setPreviewViewMode('nominees');
        }
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to generate PDF',
          severity: 'error',
        });
        setPreviewViewMode('nominees');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to generate PDF preview',
        severity: 'error',
      });
      setPreviewViewMode('nominees');
    }

    setPdfLoading(false);
  };

  // Handle back to nominees list
  const handleBackToNominees = () => {
    setPreviewViewMode('nominees');
    if (pdfPreviewUrl && pdfPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setSelectedNominee(null);
  };

  const handleGenerateAndShare = async (declaration) => {
    try {
      const result = await lifeDeclarationService.generateAndShare(declaration.id);
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Certificate generated and shared successfully',
          severity: 'success',
        });
        handleCloseActionDialog();
        fetchDeclarations();
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error',
        });
      }
    } catch (error) {
      // Error handled by caller or snackbar
    }
  };

  const handleActionFormChange = (field, value) => {
    setActionFormData({ ...actionFormData, [field]: value });
  };

  const handleSaveAction = async () => {
    if (!selectedActionType) {
      setSnackbar({
        open: true,
        message: 'Please select an action type',
        severity: 'error',
      });
      return;
    }

    // For preview action, call the preview handler instead
    if (selectedActionType === 'preview') {
      handlePreviewCertificate(selectedDeclaration);
      return;
    }

    // Build payload based on API specification
    let payload = {
      action_type: selectedActionType,
    };

    // Include admin_remarks if provided (for all actions)
    if (actionFormData.admin_remarks) {
      payload.admin_remarks = actionFormData.admin_remarks;
    }

    // For mark_life_declaration, use attachment_file field
    if (selectedActionType === 'mark_life_declaration' && actionFormData.supportingDoc) {
      payload.attachment_file = actionFormData.supportingDoc;
    }

    // For mark_demise_confirmation, use supporting_doc_file field
    if (selectedActionType === 'mark_demise_confirmation' && actionFormData.supportingDoc) {
      payload.supporting_doc_file = actionFormData.supportingDoc;
    }

    // For upload_death_certificate, use certificate_file field (REQUIRED)
    if (selectedActionType === 'upload_death_certificate' && actionFormData.proofFile) {
      payload.certificate_file = actionFormData.proofFile;
    }

    // For generate_share, use supporting_doc_file field
    if (selectedActionType === 'generate_share' && actionFormData.supportingDoc) {
      payload.supporting_doc_file = actionFormData.supportingDoc;
    }

    const result = await lifeDeclarationService.takeAction(
      selectedDeclaration.user_id || selectedDeclaration.id,
      payload
    );

    if (result.success) {
      const successMessages = {
        mark_life_declaration: 'Life declaration marked successfully',
        mark_demise_confirmation: 'Demise confirmation marked successfully',
        upload_death_certificate: 'Death certificate uploaded successfully',
        generate_share: 'Documents generated and shared successfully',
      };

      setSnackbar({
        open: true,
        message: successMessages[selectedActionType] || 'Action completed successfully',
        severity: 'success',
      });
      handleCloseActionDialog();
      fetchDeclarations();
    } else {
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
    }
  };

  // View details handlers
  const handleViewDetails = async (declaration) => {
    setSelectedDeclaration(declaration);
    setDetailsLoading(true);
    setDetailsDialogOpen(true);

    const result = await lifeDeclarationService.getUserDetails(declaration.id);

    setDetailsLoading(false);

    if (result.success) {
      setSelectedUserDetails(result.data);
    } else {
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
      setDetailsDialogOpen(false);
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedUserDetails(null);
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setActionFormData({ ...actionFormData, proofFile: file });
  };

  const handleSupportingDocChange = (e) => {
    const file = e.target.files[0];
    setActionFormData({ ...actionFormData, supportingDoc: file });
  };
  // Certificate upload handlers
  const handleCertificateClick = (declaration) => {
    setSelectedDeclaration(declaration);
    setCertificateFormData({
      certificate_file: null,
      admin_remarks: '',
      mark_as_valid: false,
      update_demise_status: false,
    });
    setCertificateDialogOpen(true);
  };

  const handleCloseCertificateDialog = () => {
    setCertificateDialogOpen(false);
    setSelectedDeclaration(null);
  };

  const handleCertificateFileChange = (e) => {
    const file = e.target.files[0];
    setCertificateFormData({ ...certificateFormData, certificate_file: file });
  };

  const handleCertificateFormChange = (field, value) => {
    setCertificateFormData({ ...certificateFormData, [field]: value });
  };

  const handleUploadCertificate = async () => {
    if (!certificateFormData.certificate_file) {
      setSnackbar({
        open: true,
        message: 'Please select a certificate file',
        severity: 'error',
      });
      return;
    }

    const result = await lifeDeclarationService.uploadCertificate(
      selectedDeclaration.id,
      certificateFormData.certificate_file,
      {
        admin_remarks: certificateFormData.admin_remarks,
        mark_as_valid: certificateFormData.mark_as_valid,
        update_demise_status: certificateFormData.update_demise_status,
      }
    );

    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Certificate uploaded successfully',
        severity: 'success',
      });
      handleCloseCertificateDialog();
      fetchDeclarations();
    } else {
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
    }
  };

  // Get status chip color
  const getStatusChipColor = (status) => {
    switch (status) {
      case 'alive':
        return { bgcolor: '#D4F4DD', color: '#2E7D32' };
      case 'pending':
        return { bgcolor: '#FFF3E0', color: '#E65100' };
      case 'presumed_deceased':
        return { bgcolor: '#FFF3E0', color: '#E65100' };
      case 'confirmed':
      case 'confirmed_deceased':
        return { bgcolor: '#D4F4DD', color: '#2E7D32' };
      default:
        return { bgcolor: '#F5F5F5', color: '#616161' };
    }
  };

  // Get DC Availability display (Yes or No based on has_death_certificate)
  const getDCAvailabilityDisplay = (hasCertificate) => {
    return hasCertificate ? 'Received' : 'Pending';
  };

  // Get DC Availability chip color
  const getDCAvailabilityColor = (hasCertificate) => {
    if (hasCertificate) {
      return { bgcolor: '#D4F4DD', color: '#2E7D32' };
    }
    return { bgcolor: '#FFF3E0', color: '#E65100' };
  };

  // Get DC Validity display
  const getDCValidityDisplay = (status) => {
    if (!status || status === 'none') return 'None';
    return formatStatus(status);
  };

  // Get DC Validity chip color
  const getDCValidityColor = (status) => {
    if (!status || status === 'none') {
      return { bgcolor: '#F5F5F5', color: '#616161' };
    }
    if (status === 'valid') {
      return { bgcolor: '#D4F4DD', color: '#2E7D32' };
    }
    if (status === 'invalid') {
      return { bgcolor: '#FFEBEE', color: '#C62828' };
    }
    return { bgcolor: '#FFF3E0', color: '#E65100' };
  };

  // Get Life Cycle display
  const getLifeCycleDisplay = (status) => {
    if (!status) return '-';
    return formatStatus(status);
  };

  // Get Life Cycle chip color
  const getLifeCycleColor = (status) => {
    if (status === 'open') {
      return { bgcolor: '#E3F2FD', color: '#1565C0' };
    }
    if (status === 'closed') {
      return { bgcolor: '#F5F5F5', color: '#616161' };
    }
    return { bgcolor: '#F5F5F5', color: '#616161' };
  };

  // Format status text
  const formatStatus = (status) => {
    if (!status) return '-';
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Format date as dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}
          >
            Manage Due Life Declaration
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearch}
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover fieldset': { borderColor: 'transparent' },
                  '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Filter">
              <IconButton
                onClick={() => setFilterDialogOpen(true)}
                sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#F8FAFC' } }}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh (clear filters)">
              <IconButton
                onClick={handleRefresh}
                sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#F8FAFC' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #ECF0F1',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : declarations.length === 0 ? (
          <Box sx={{ padding: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">No due life declarations found</Typography>
          </Box>
        ) : !isMobile ? (
          // Desktop Table View
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 900, sm: 'auto' } }}>
                <TableHead>
                  <TableRow
                    sx={{
                      background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                    }}
                  >
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      User ID
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      User Name
                    </TableCell>

                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      Life Declaration Due Date
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      Demise Status
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      DC Availability
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      DC Validity
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      Life Cycle
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', padding: '16px' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {declarations.map((declaration, index) => {
                    const statusColors = getStatusChipColor(declaration.demise_status);
                    return (
                      <TableRow
                        key={declaration.user_id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                          '&:hover': { backgroundColor: '#ECF0F1' },
                        }}
                      >
                        <TableCell sx={{ color: '#2C3E50', fontSize: '14px', padding: '16px' }}>
                          {declaration.user_id || '-'}
                        </TableCell>
                        <TableCell sx={{ color: '#2C3E50', fontSize: '14px', padding: '16px' }}>
                          {declaration.user_name || declaration.full_name || '-'}
                        </TableCell>
                        <TableCell sx={{ color: '#2C3E50', fontSize: '14px', padding: '16px' }}>
                          {declaration.declaration_due_date ? declaration.declaration_due_date : '-'}
                        </TableCell>
                        <TableCell sx={{ padding: '16px' }}>
                          <Chip
                            label={declaration.demise_status_display || formatStatus(declaration.demise_status) || '-'}
                            size="small"
                            sx={{
                              ...getStatusChipColor(declaration.demise_status),
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: '16px' }}>
                          <Chip
                            label={getDCAvailabilityDisplay(declaration.has_death_certificate)}
                            size="small"
                            sx={{
                              ...getDCAvailabilityColor(declaration.has_death_certificate),
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: '16px' }}>
                          <Chip
                            label={getDCValidityDisplay(declaration.death_certificate_status)}
                            size="small"
                            sx={{
                              ...getDCValidityColor(declaration.death_certificate_status),
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: '16px' }}>
                          <Chip
                            label={getLifeCycleDisplay(declaration.life_cycle_status)}
                            size="small"
                            sx={{
                              ...getLifeCycleColor(declaration.life_cycle_status),
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: '16px' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>

                            <Button
                              size="small"
                              onClick={() => handleActionClick(declaration)}
                              sx={{
                                color: '#4E83BD',
                                backgroundColor: 'rgba(78, 131, 189, 0.1)',
                                textTransform: 'none',
                                fontSize: '12px',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: 'rgba(78, 131, 189, 0.2)',
                                },
                              }}
                            >
                              Action
                            </Button>

                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid #ECF0F1',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  color: '#2C3E50',
                },
              }}
            />
          </>
        ) : (
          // Mobile Card View
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {declarations.map((declaration) => (
              <Paper
                key={declaration.user_id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid #ECF0F1',
                  backgroundColor: '#FFFFFF',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(78, 131, 189, 0.15)',
                    borderColor: '#4E83BD',
                  },
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{
                    backgroundColor: expandedCardId === declaration.user_id ? '#3A6A99' : '#4E83BD',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  <Box>
                    <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>
                      {declaration.user_id}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                      {declaration.user_name || '-'}
                    </Typography>
                  </Box>
                </Box>

                {/* Card Title - Status Chips */}
                <Box
                  onClick={() => setExpandedCardId(expandedCardId === declaration.user_id ? null : declaration.user_id)}
                  sx={{
                    padding: '14px 16px',
                    backgroundColor: '#FFFFFF',
                    borderBottom: expandedCardId === declaration.user_id ? '1px solid #ECF0F1' : 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    '&:active': {
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 1,
                      }}
                    >
                      Due Date
                    </Typography>
                    <Typography sx={{ color: '#1E293B', fontSize: '13px', fontWeight: 600 }}>
                      {declaration.last_failure_at ? formatDate(declaration.last_failure_at) : '-'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    sx={{
                      color: '#4E83BD',
                      flexShrink: 0,
                      transition: 'transform 0.3s ease',
                      transform: expandedCardId === declaration.user_id ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    {expandedCardId === declaration.user_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded Content */}
                <Collapse in={expandedCardId === declaration.user_id} timeout="auto" unmountOnExit>
                  <Box sx={{ padding: '14px 16px', backgroundColor: '#F8FAFC', borderBottom: '1px solid #ECF0F1' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, marginBottom: 2 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            marginBottom: 0.5,
                          }}
                        >
                          Demise Status
                        </Typography>
                        <Chip
                          label={declaration.demise_status_display || formatStatus(declaration.demise_status) || '-'}
                          size="small"
                          sx={{
                            ...getStatusChipColor(declaration.demise_status),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            marginBottom: 0.5,
                          }}
                        >
                          DC Availability
                        </Typography>
                        <Chip
                          label={getDCAvailabilityDisplay(declaration.has_death_certificate)}
                          size="small"
                          sx={{
                            ...getDCAvailabilityColor(declaration.has_death_certificate),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            marginBottom: 0.5,
                          }}
                        >
                          DC Validity
                        </Typography>
                        <Chip
                          label={getDCValidityDisplay(declaration.death_certificate_status)}
                          size="small"
                          sx={{
                            ...getDCValidityColor(declaration.death_certificate_status),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            marginBottom: 0.5,
                          }}
                        >
                          Life Cycle
                        </Typography>
                        <Chip
                          label={getLifeCycleDisplay(declaration.life_cycle_status)}
                          size="small"
                          sx={{
                            ...getLifeCycleColor(declaration.life_cycle_status),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Collapse>

                {/* Card Actions */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '10px 12px',
                    borderTop: '1px solid #ECF0F1',
                    backgroundColor: '#F8FAFC',
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => handleActionClick(declaration)}
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      textTransform: 'none',
                      fontSize: '12px',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(78, 131, 189, 0.2)',
                      },
                    }}
                  >
                    Action
                  </Button>
                </Box>
              </Paper>
            ))}

            {/* Mobile Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid #ECF0F1',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  color: '#2C3E50',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2C3E50' }}>
              Filter Declarations
            </Typography>
            <IconButton onClick={() => setFilterDialogOpen(false)} sx={{ color: '#2C3E50' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              select
              fullWidth
              label="Demise Status"
              value={filters.demise_status}
              onChange={(e) => handleFilterChange('demise_status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="Life Cycle Status"
              value={filters.life_cycle_status}
              onChange={(e) => handleFilterChange('life_cycle_status', e.target.value)}
            >
              <MenuItem value="">All (open & closed)</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Minimum Failure Count"
              type="number"
              value={filters.min_failures}
              onChange={(e) => handleFilterChange('min_failures', e.target.value)}
              inputProps={{ min: 0 }}
              placeholder="Default: 1"
            />

            <TextField
              select
              fullWidth
              label="Notification Sent"
              value={filters.notification_sent}
              onChange={(e) => handleFilterChange('notification_sent', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="Has Certificate"
              value={filters.has_certificate}
              onChange={(e) => handleFilterChange('has_certificate', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, marginTop: 3 }}>
            <Button variant="outlined" onClick={clearFilters} sx={{ textTransform: 'none' }}>
              Clear All
            </Button>
            <Button
              variant="contained"
              onClick={applyFilters}
              sx={{
                textTransform: 'none',
                backgroundColor: '#4E83BD',
                '&:hover': { backgroundColor: '#3A6A99' },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '18px',
            }}
          >
            Life Declaration Action
          </Typography>
          <IconButton
            onClick={handleCloseActionDialog}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              minHeight: '400px',
            }}
          >
            {/* Left Column - Action Selection */}
            <Box
              sx={{
                flex: '0 0 40%',
                padding: 3,
                backgroundColor: '#FFFFFF',
                borderRight: { md: '1px solid #ECF0F1' },
                borderBottom: { xs: '1px solid #ECF0F1', md: 'none' },
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  marginBottom: 3,
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#4E83BD',
                    borderRadius: '2px',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#1E293B',
                    fontSize: '16px',
                  }}
                >
                  User Information
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                User ID
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={selectedDeclaration?.user_id || selectedDeclaration?.id || ''}
                disabled
                sx={{
                  marginBottom: 2.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#F8FAFC',
                    '& fieldset': {
                      borderColor: '#E2E8F0',
                    },
                  },
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Action Type <span style={{ color: '#DC2626' }}>*</span>
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={selectedActionType}
                onChange={(e) => handleActionTypeChange(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#F8FAFC',
                    '& fieldset': {
                      borderColor: '#E2E8F0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#4E83BD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4E83BD',
                    },
                  },
                }}
              >
                <MenuItem value="">Select Action</MenuItem>
                {selectedDeclaration?.available_actions?.map((action) => (
                  <MenuItem
                    key={action.action_type}
                    value={action.action_type}
                    disabled={!action.enabled}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        {action.label}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  marginTop: 4,
                  paddingTop: 2,
                  borderTop: '1px solid #E2E8F0',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseActionDialog}
                  disabled={actionLoading}
                  sx={{
                    borderColor: '#E2E8F0',
                    color: '#64748b',
                    textTransform: 'none',
                    borderRadius: '10px',
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#CBD5E1',
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  Cancel
                </Button>
                <ActionButton
                  variant="contained"
                  loading={actionLoading || pdfLoading}
                  onClick={() => runSaveAction(handleSaveAction)}
                  loadingText="Saving..."
                  disabled={
                    pdfLoading ||
                    !selectedActionType ||
                    (selectedActionType === 'upload_death_certificate' && !actionFormData.proofFile) ||
                    selectedActionType === 'preview'
                  }
                  sx={{
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                    textTransform: 'none',
                    borderRadius: '10px',
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(78, 131, 189, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3A6A99 0%, #2D5580 100%)',
                      boxShadow: '0 6px 16px rgba(78, 131, 189, 0.5)',
                    },
                    '&.Mui-disabled': {
                      background: '#E2E8F0',
                      color: '#94a3b8',
                    },
                  }}
                >
                  Save Action
                </ActionButton>
              </Box>
            </Box>

            {/* Right Column - Action Details */}
            <Box
              sx={{
                flex: '0 0 60%',
                padding: 3,
                backgroundColor: '#F8FAFC',
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  marginBottom: 3,
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#10B981',
                    borderRadius: '2px',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#1E293B',
                    fontSize: '16px',
                  }}
                >
                  Action Details
                </Typography>
              </Box>

              {/* Show additional fields based on selected action */}
              {selectedActionType === 'mark_life_declaration' ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Admin Remarks
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={actionFormData.admin_remarks}
                    onChange={(e) => handleActionFormChange('admin_remarks', e.target.value)}
                    placeholder="Enter remarks for marking life declaration..."
                    sx={{
                      marginBottom: 2.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': { borderColor: '#E2E8F0' },
                        '&:hover fieldset': { borderColor: '#4E83BD' },
                        '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                      },
                    }}
                  />

                  {/* Supporting Document Upload */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Supporting Document (Optional)
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px',
                      border: '2px dashed #CBD5E1',
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      marginBottom: 2.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#4E83BD',
                        backgroundColor: '#F8FAFC',
                      },
                    }}
                    component="label"
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 1.5,
                      }}
                    >
                      <Typography sx={{ color: '#4E83BD', fontSize: '20px' }}>↑</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                      {actionFormData.supportingDoc ? actionFormData.supportingDoc.name : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', marginTop: 0.5 }}>
                      PDF, JPG, PNG (Max 10MB)
                    </Typography>
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleSupportingDocChange}
                    />
                  </Box>

                  {/* Info Box */}
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#D4F4DD',
                      borderRadius: '10px',
                      border: '1px solid #C8E6C9',
                      marginBottom: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', color: '#2E7D32', fontWeight: 500 }}>
                      This action will mark the user's life declaration as completed and reset the failure count.
                    </Typography>
                  </Box>

                  {/* Current Status */}
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#FFFFFF',
                      borderRadius: '10px',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 1.5,
                      }}
                    >
                      Current Status
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', marginBottom: 0.5 }}>
                          Demise Status
                        </Typography>
                        <Chip
                          label={formatStatus(selectedDeclaration?.demise_status) || '-'}
                          size="small"
                          sx={{
                            ...getStatusChipColor(selectedDeclaration?.demise_status),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', marginBottom: 0.5 }}>
                          DC Availability
                        </Typography>
                        <Chip
                          label={getDCAvailabilityDisplay(selectedDeclaration?.has_death_certificate)}
                          size="small"
                          sx={{
                            ...getDCAvailabilityColor(selectedDeclaration?.has_death_certificate),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : selectedActionType === 'mark_demise_confirmation' ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Admin Remarks
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={actionFormData.admin_remarks}
                    onChange={(e) => handleActionFormChange('admin_remarks', e.target.value)}
                    placeholder="Enter remarks for demise confirmation..."
                    sx={{
                      marginBottom: 2.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': { borderColor: '#E2E8F0' },
                        '&:hover fieldset': { borderColor: '#4E83BD' },
                        '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                      },
                    }}
                  />

                  {/* Supporting Document Upload */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Supporting Document (Optional)
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px',
                      border: '2px dashed #CBD5E1',
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      marginBottom: 2.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#4E83BD',
                        backgroundColor: '#F8FAFC',
                      },
                    }}
                    component="label"
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 1.5,
                      }}
                    >
                      <Typography sx={{ color: '#4E83BD', fontSize: '20px' }}>↑</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                      {actionFormData.supportingDoc ? actionFormData.supportingDoc.name : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', marginTop: 0.5 }}>
                      PDF, JPG, PNG (Max 10MB)
                    </Typography>
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleSupportingDocChange}
                    />
                  </Box>

                  {/* Warning Box */}
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#FFF3E0',
                      borderRadius: '10px',
                      border: '1px solid #FFE0B2',
                      marginBottom: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', color: '#E65100', fontWeight: 500 }}>
                      This action will mark the user as deceased. Please ensure you have verified the information before proceeding.
                    </Typography>
                  </Box>

                  {/* Current Status */}
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#FFFFFF',
                      borderRadius: '10px',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 1.5,
                      }}
                    >
                      Current Status
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', marginBottom: 0.5 }}>
                          Demise Status
                        </Typography>
                        <Chip
                          label={formatStatus(selectedDeclaration?.demise_status) || '-'}
                          size="small"
                          sx={{
                            ...getStatusChipColor(selectedDeclaration?.demise_status),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', marginBottom: 0.5 }}>
                          DC Availability
                        </Typography>
                        <Chip
                          label={getDCAvailabilityDisplay(selectedDeclaration?.has_death_certificate)}
                          size="small"
                          sx={{
                            ...getDCAvailabilityColor(selectedDeclaration?.has_death_certificate),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : selectedActionType === 'upload_death_certificate' ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Death Certificate File <span style={{ color: '#DC2626' }}>*</span>
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      border: '2px dashed #E2E8F0',
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      marginBottom: 2.5,
                    }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        color: '#4E83BD',
                        borderColor: '#4E83BD',
                        borderRadius: '8px',
                        marginRight: 2,
                        '&:hover': {
                          borderColor: '#3A6A99',
                          backgroundColor: 'rgba(78, 131, 189, 0.08)',
                        },
                      }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                    </Button>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: actionFormData.proofFile ? '#1E293B' : '#94a3b8',
                      }}
                    >
                      {actionFormData.proofFile ? actionFormData.proofFile.name : 'No file chosen (PDF, JPG, PNG)'}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Admin Remarks
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={actionFormData.admin_remarks}
                    onChange={(e) => handleActionFormChange('admin_remarks', e.target.value)}
                    placeholder="Enter remarks..."
                    sx={{
                      marginBottom: 2.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': { borderColor: '#E2E8F0' },
                        '&:hover fieldset': { borderColor: '#4E83BD' },
                        '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                      },
                    }}
                  />

                  {/* Info Box */}
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#E3F2FD',
                      borderRadius: '10px',
                      border: '1px solid #BBDEFB',
                      marginBottom: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', color: '#1565C0', fontWeight: 500 }}>
                      After uploading, the certificate will be marked as "Submitted". You can verify and mark it as Valid or Invalid separately.
                    </Typography>
                  </Box>

                  {/* Current Status */}
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#FFFFFF',
                      borderRadius: '10px',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 1.5,
                      }}
                    >
                      Current Status
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', marginBottom: 0.5 }}>
                          Demise Status
                        </Typography>
                        <Chip
                          label={formatStatus(selectedDeclaration?.demise_status) || '-'}
                          size="small"
                          sx={{
                            ...getStatusChipColor(selectedDeclaration?.demise_status),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', marginBottom: 0.5 }}>
                          DC Availability
                        </Typography>
                        <Chip
                          label={getDCAvailabilityDisplay(selectedDeclaration?.has_death_certificate)}
                          size="small"
                          sx={{
                            ...getDCAvailabilityColor(selectedDeclaration?.has_death_certificate),
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : selectedActionType === 'preview' ? (
                <>
                  {nomineesLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        minHeight: '400px',
                      }}
                    >
                      <CircularProgress sx={{ color: '#4E83BD' }} />
                      <Typography sx={{ marginTop: 2, color: '#64748b', fontSize: '14px' }}>
                        Loading Nominees...
                      </Typography>
                    </Box>
                  ) : previewViewMode === 'pdf' || pdfLoading ? (
                    // PDF Preview Mode
                    <Box sx={{ height: '100%', minHeight: '500px' }}>
                      {/* Back Button */}
                      <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackToNominees}
                        disabled={pdfLoading}
                        sx={{
                          marginBottom: 2,
                          color: '#4E83BD',
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                          },
                        }}
                      >
                        Back to Nominees
                      </Button>
                      {pdfLoading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '450px',
                          }}
                        >
                          <CircularProgress sx={{ color: '#4E83BD' }} />
                          <Typography sx={{ marginTop: 2, color: '#64748b', fontSize: '14px' }}>
                            Generating PDF for {selectedNominee?.nominee_name}...
                          </Typography>
                        </Box>
                      ) : pdfPreviewUrl ? (
                        <>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1E293B', marginBottom: 1 }}>
                            PDF Preview for: {selectedNominee?.nominee_name}
                          </Typography>
                          {isMobile ? (
                            // Mobile: Show button to open PDF externally
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                padding: 4,
                                height: '400px',
                                backgroundColor: '#F8FAFC',
                                borderRadius: '10px',
                                border: '1px solid #E2E8F0',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 2,
                                  bgcolor: '#DC2626',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: 700,
                                }}
                              >
                                PDF
                              </Box>
                              <Typography sx={{ fontSize: '13px', color: '#64748B', textAlign: 'center' }}>
                                PDF preview is not supported on mobile browsers
                              </Typography>
                              <Button
                                variant="contained"
                                onClick={() => window.open(pdfPreviewUrl, '_blank')}
                                sx={{
                                  bgcolor: '#4E83BD',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  '&:hover': { bgcolor: '#3A6A99' },
                                }}
                              >
                                Open PDF
                              </Button>
                            </Box>
                          ) : (
                            // Desktop: Use iframe for PDF preview
                            <iframe
                              src={pdfPreviewUrl}
                              style={{
                                width: '100%',
                                height: '450px',
                                border: '1px solid #E2E8F0',
                                borderRadius: '10px',
                              }}
                              title="PDF Preview"
                            />
                          )}
                        </>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '450px',
                            color: '#94a3b8',
                          }}
                        >
                          <Typography sx={{ fontSize: '14px', textAlign: 'center' }}>
                            Failed to load PDF. Please try again.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : nomineesList.length > 0 ? (
                    // Nominees List Mode
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#64748b',
                          marginBottom: 2,
                        }}
                      >
                        Select a nominee to preview their PDF document ({nomineesList.length} nominees found)
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {nomineesList.map((nominee) => (
                          <Box
                            key={nominee.nominee_id}
                            onClick={() => handleNomineeClick(nominee)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: { xs: '12px', sm: '16px' },
                              backgroundColor: '#FFFFFF',
                              borderRadius: '12px',
                              border: '1px solid #E2E8F0',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: '#4E83BD',
                                backgroundColor: '#F8FAFC',
                                boxShadow: '0 2px 8px rgba(78, 131, 189, 0.15)',
                              },
                            }}
                          >
                            {/* Nominee Avatar */}
                            <Box
                              sx={{
                                width: { xs: '40px', sm: '48px' },
                                height: { xs: '40px', sm: '48px' },
                                minWidth: { xs: '40px', sm: '48px' },
                                borderRadius: '50%',
                                backgroundColor: '#E3F2FD',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: { xs: 1.5, sm: 2 },
                              }}
                            >
                              <PersonIcon sx={{ color: '#4E83BD', fontSize: { xs: '20px', sm: '24px' } }} />
                            </Box>

                            {/* Nominee Info */}
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <Typography
                                sx={{
                                  fontSize: { xs: '14px', sm: '15px' },
                                  fontWeight: 600,
                                  color: '#1E293B',
                                  marginBottom: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {nominee.nominee_name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: { xs: '12px', sm: '13px' },
                                  color: '#64748b',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {nominee.relationship?.relationship_name || '-'} • {nominee.email}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, marginTop: 1 }}>
                                <Chip
                                  size="small"
                                  label={`${nominee.total_assets_count} Asset${nominee.total_assets_count !== 1 ? 's' : ''}`}
                                  sx={{
                                    fontSize: { xs: '10px', sm: '11px' },
                                    height: { xs: '20px', sm: '22px' },
                                    backgroundColor: '#E3F2FD',
                                    color: '#1565C0',
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={`${nominee.total_share_percentage}% Share`}
                                  sx={{
                                    fontSize: { xs: '10px', sm: '11px' },
                                    height: { xs: '20px', sm: '22px' },
                                    backgroundColor: '#D4F4DD',
                                    color: '#2E7D32',
                                  }}
                                />
                                {nominee.email_verified && (
                                  <Chip
                                    size="small"
                                    label="Email Verified"
                                    sx={{
                                      fontSize: { xs: '10px', sm: '11px' },
                                      height: { xs: '20px', sm: '22px' },
                                      backgroundColor: '#D4F4DD',
                                      color: '#2E7D32',
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            {/* PDF Icon */}
                            <Box
                              sx={{
                                width: { xs: '36px', sm: '40px' },
                                height: { xs: '36px', sm: '40px' },
                                minWidth: { xs: '36px', sm: '40px' },
                                borderRadius: '8px',
                                backgroundColor: '#FFF3E0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: 1,
                              }}
                            >
                              <PictureAsPdfIcon sx={{ color: '#E65100', fontSize: { xs: '18px', sm: '22px' } }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        minHeight: '200px',
                        color: '#94a3b8',
                      }}
                    >
                      <Typography sx={{ fontSize: '14px', textAlign: 'center' }}>
                        No nominees available for PDF preview
                      </Typography>
                    </Box>
                  )}
                </>
              ) : selectedActionType === 'generate_share' ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: '200px',
                  }}
                >
                  {/* Supporting Document Upload */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Supporting Document (Optional)
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px',
                      border: '2px dashed #CBD5E1',
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      marginBottom: 2.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#4E83BD',
                        backgroundColor: '#F8FAFC',
                      },
                    }}
                    component="label"
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 1.5,
                      }}
                    >
                      <Typography sx={{ color: '#4E83BD', fontSize: '20px' }}>↑</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                      {actionFormData.supportingDoc ? actionFormData.supportingDoc.name : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', marginTop: 0.5 }}>
                      PDF, JPG, PNG (Max 10MB)
                    </Typography>
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleSupportingDocChange}
                    />
                  </Box>

                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#E3F2FD',
                      borderRadius: '10px',
                      border: '1px solid #BBDEFB',
                      marginBottom: 2,
                      width: '100%',
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', color: '#1565C0', fontWeight: 500 }}>
                      This action will generate PDF documents and share them with all nominees who have shared assets or confidential notes.
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '14px', color: '#64748b', textAlign: 'center' }}>
                    Click "Save Action" to generate and share documents with nominees
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: '200px',
                    color: '#94a3b8',
                  }}
                >
                  <Typography sx={{ fontSize: '14px', textAlign: 'center' }}>
                    Select an action type from the left panel to view additional options
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2C3E50' }}>
              User Details
            </Typography>
            <IconButton onClick={handleCloseDetailsDialog} sx={{ color: '#2C3E50' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedUserDetails ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ padding: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4E83BD', marginBottom: 2 }}>
                    Basic Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Name:</strong> {selectedUserDetails.user?.full_name || selectedUserDetails.full_name || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {selectedUserDetails.user?.email || selectedUserDetails.email || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Phone:</strong> {selectedUserDetails.user?.phone || selectedUserDetails.phone || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>User ID:</strong> {selectedUserDetails.id || '-'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ padding: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4E83BD', marginBottom: 2 }}>
                    Declaration Status
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Demise Status:</strong>{' '}
                      <Chip
                        label={formatStatus(selectedUserDetails.demise_status)}
                        size="small"
                        sx={{
                          ...getStatusChipColor(selectedUserDetails.demise_status),
                          fontWeight: 600,
                        }}
                      />
                    </Typography>
                    <Typography variant="body2">
                      <strong>Failure Count:</strong> {selectedUserDetails.failure_count || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Has Certificate:</strong> {selectedUserDetails.has_certificate ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Notification Sent:</strong> {selectedUserDetails.notification_sent ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {selectedUserDetails.nominees && selectedUserDetails.nominees.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ padding: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4E83BD', marginBottom: 2 }}>
                      Nominees
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Relationship</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedUserDetails.nominees.map((nominee, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{nominee.name || '-'}</TableCell>
                              <TableCell>{nominee.email || '-'}</TableCell>
                              <TableCell>{nominee.phone || '-'}</TableCell>
                              <TableCell>{nominee.relationship || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}

              {selectedUserDetails.life_declaration_history && selectedUserDetails.life_declaration_history.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ padding: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4E83BD', marginBottom: 2 }}>
                      Life Declaration History
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedUserDetails.life_declaration_history.map((history, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{formatDate(history.created_at || history.date)}</TableCell>
                              <TableCell>{formatStatus(history.status)}</TableCell>
                              <TableCell>{history.remarks || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}

              {selectedUserDetails.subscription_status && (
                <Grid item xs={12}>
                  <Paper sx={{ padding: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4E83BD', marginBottom: 2 }}>
                      Subscription Status
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2">
                        <strong>Plan:</strong> {selectedUserDetails.subscription_status.plan_name || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Status:</strong> {selectedUserDetails.subscription_status.status || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Valid Until:</strong> {formatDate(selectedUserDetails.subscription_status.valid_until)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography color="textSecondary">No details available</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Certificate Upload Dialog */}
      <Dialog
        open={certificateDialogOpen}
        onClose={handleCloseCertificateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, padding: 1 } }}
      >
        <DialogContent sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', color: '#2C3E50' }}>
              Upload Death Certificate
            </Typography>
            <IconButton onClick={handleCloseCertificateDialog} sx={{ color: '#2C3E50', padding: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ marginBottom: 2.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2C3E50', marginBottom: 1 }}>
              User ID
            </Typography>
            <TextField
              fullWidth
              value={selectedDeclaration?.id || ''}
              disabled
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#F8FAFC',
                },
              }}
            />
          </Box>

          <Box sx={{ marginBottom: 2.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2C3E50', marginBottom: 1 }}>
              Certificate File *
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                border: '1px solid #D1D9E0',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Button
                variant="outlined"
                component="label"
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  color: '#2C3E50',
                  borderColor: '#D1D9E0',
                  marginRight: 2,
                }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleCertificateFileChange}
                />
              </Button>
              <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                {certificateFormData.certificate_file
                  ? certificateFormData.certificate_file.name
                  : 'No file chosen (PDF, JPG, PNG)'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ marginBottom: 2.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2C3E50', marginBottom: 1 }}>
              Admin Remarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={certificateFormData.admin_remarks}
              onChange={(e) => handleCertificateFormChange('admin_remarks', e.target.value)}
              placeholder="Enter remarks..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                },
              }}
            />
          </Box>

          <Box sx={{ marginBottom: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={certificateFormData.update_demise_status}
                  onChange={(e) => handleCertificateFormChange('update_demise_status', e.target.checked)}
                  sx={{ color: '#4E83BD', '&.Mui-checked': { color: '#4E83BD' } }}
                />
              }
              label="Update Demise Status to Confirmed Deceased"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleCloseCertificateDialog}
              disabled={certificateLoading}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                color: '#2C3E50',
                borderColor: '#D1D9E0',
                paddingX: 3,
              }}
            >
              Cancel
            </Button>
            <ActionButton
              variant="contained"
              loading={certificateLoading}
              onClick={() => runUploadCertificate(handleUploadCertificate)}
              loadingText="Uploading..."
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                backgroundColor: '#4E83BD',
                color: 'white',
                paddingX: 3,
                fontWeight: 600,
                '&:hover': { backgroundColor: '#3A6A99' },
              }}
            >
              Upload
            </ActionButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LifeDeclaration;
